/**
 * instalacao.ts — Saber em que aparelho a pessoa está e se dá para instalar.
 *
 * POR QUE ISTO EXISTE. Instalar o app é passo manual e DIFERENTE em cada
 * celular: no Android é um botão que o Chrome oferece; no iPhone é
 * Compartilhar → "Adicionar à Tela de Início", e só pelo Safari — o Chrome do
 * iOS não instala nada, por decisão da Apple. Explicar isso por WhatsApp para
 * a equipe inteira, com aparelhos diferentes, é onde a instalação morre. A tela
 * `/public/instalar` lê o aparelho e mostra UM caminho, o certo.
 *
 * `beforeinstallprompt` é capturado no `main.tsx`, no boot: o Chrome dispara
 * esse evento uma vez e cedo, e quem não o guardar perde a chance de oferecer
 * o botão depois. É por isso que a captura não mora aqui dentro da tela.
 *
 * Portado do projeto irmão `webapp-rm-ops` (08/08/2026).
 */

/** O evento do Chrome, que o TypeScript não conhece por não ser padrão. */
export interface EventoInstalacao extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Guardado no boot pelo `main.tsx` — ver o comentário de lá. */
let promptGuardado: EventoInstalacao | null = null;

export function guardarPrompt(e: EventoInstalacao): void {
  promptGuardado = e;
}

export function temPromptNativo(): boolean {
  return promptGuardado !== null;
}

/** Abre a caixa de instalação do Chrome. Devolve se a pessoa aceitou. */
export async function instalarAgora(): Promise<boolean> {
  if (!promptGuardado) return false;
  await promptGuardado.prompt();
  const { outcome } = await promptGuardado.userChoice;
  // O evento é de uso único: depois de usado, o Chrome não o devolve.
  if (outcome === 'accepted') promptGuardado = null;
  return outcome === 'accepted';
}

export type Aparelho = 'android' | 'ios' | 'desktop';
export type Navegador = 'chrome' | 'safari' | 'outro-ios' | 'outro';

export interface Ambiente {
  aparelho: Aparelho;
  navegador: Navegador;
  /** Já está rodando como app instalado? */
  instalado: boolean;
  /** Está dentro do navegador embutido do WhatsApp/Instagram? */
  dentroDeApp: boolean;
}

export function detectar(): Ambiente {
  const ua = navigator.userAgent;

  // iPad moderno se declara como Mac; o toque é o que o entrega.
  const ehIOS =
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document);
  const ehAndroid = /Android/.test(ua);

  const aparelho: Aparelho = ehIOS ? 'ios' : ehAndroid ? 'android' : 'desktop';

  let navegador: Navegador = 'outro';
  if (ehIOS) {
    // No iOS TODO navegador usa o motor do Safari, mas só o Safari de verdade
    // instala. CriOS = Chrome, FxiOS = Firefox, EdgiOS = Edge.
    navegador = /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua) ? 'outro-ios' : 'safari';
  } else if (/Chrome|Chromium|Edg\//.test(ua)) {
    navegador = 'chrome';
  } else if (/Safari/.test(ua)) {
    navegador = 'safari';
  }

  // `standalone` é o jeito do iOS; `display-mode` é o padrão dos outros.
  const instalado =
    window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;

  // O navegador embutido do WhatsApp não instala nada — e é por ele que o
  // link chega, já que o convite vai por WhatsApp.
  const dentroDeApp = /\bwv\b|WhatsApp|FBAN|FBAV|Instagram|Line\//.test(ua);

  return { aparelho, navegador, instalado, dentroDeApp };
}
